import React from 'react';
import {
  BRANCH_STEM,
  CARD_W,
  CIRCLE_R,
  POLL_AUDIO_HEADER_H,
  POLL_AUDIO_SLOT_LBL_H,
  POLL_AUDIO_FILE_H,
  POLL_AUDIO_FILE_GAP,
  POLL_AUDIO_EMPTY_H,
  POLL_AUDIO_ADD_BTN_H,
  POLL_AUDIO_SLOT_GAP,
  PLUS_R,
  C, ny, audioUrl, normalizeAudioField,
} from '../flowCanvas.constants';
import AudioWaveformCell from './AudioWaveformCell';
import audioLoopIcon from '../../v2/CustomisationWorkspace/WorkspaceAssets/ModuleIcons/audio_loop.png';

const BORDER_R   = 8;
const BODY_PAD   = 10;
const TOP_PAD    = 20; // extra breathing room above the card
const BOTTOM_PAD = 52;
const WAVE_H     = 26; // must match AudioWaveformCell default height

/**
 * PollAudioNode — displays configured audio files per slot with waveform / volume
 * preview. Clicking opens the audio settings panel on the right.
 *
 * Slot display rules
 * ─────────────────
 * • Toggle OFF, or (single item with playSFX=false)  → show "＋ Click to add audio" CTA
 *   – Clicking the CTA enables the toggle and opens the settings panel
 * • Toggle ON, files exist                           → show each file row
 *   – Files with playSFX=false are shown at reduced opacity (disabled / see-through)
 *   – If slot.allowMultiple an "＋ Add another file" button appears below the rows
 * • Clicking any file row                            → opens settings for that file
 * • × delete on the only remaining file              → disables toggle (back to CTA)
 * • × delete on one of several files                 → removes that file
 *
 * Props:
 *   step                  – flow step object
 *   stepX                 – absolute SVG x (centre of card)
 *   centreY               – canvas centreY
 *   isSelected            – boolean
 *   liveCustomisationData – live data object
 *   activeSlotVarName     – varName of the slot currently open in the right panel (for highlight)
 *   onActivate            – called when the outer card is clicked (unused for audio)
 *   onActivateFile        – called with { slotVarName, fileIndex } when a row / CTA is clicked
 *   onRemoveFile          – called with { slotVarName, fileIndex } when × is clicked
 */
export default function PollAudioNode({
  step,
  stepX,
  centreY,
  overrideY,
  isSelected,
  liveCustomisationData,
  activeSlotVarName,
  onActivate,
  onActivateFile,
  onRemoveFile,
  parentStepX,
  parentStepY,
  parentHalfW,
  nextStep,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  onInfoClick,
  onAddItemClick,
  addButtonLabel,
  standaloneLayout = false,
  assetFolder,
  campaignId,
}) {
  const stepY  = overrideY ?? ny(step, centreY);
  const tabKey = step.customisationConfig?.tabKey;
  const varKey = step.customisationConfig?.varKey;
  const slots  = step.customisationConfig?.variables || [];

  // ── Resolve per-slot data ───────────────────────────────────────────────
  const slotData = slots.map(slot => {
    const liveGroup = liveCustomisationData?.[tabKey]?.[varKey];
    if (!liveGroup) return { files: [], toggleEnabled: !slot.toggleVarName };

    // toggleVarName wires the slot to its individual on/off switch in toggleSFX[0]
    const toggleEnabled = slot.toggleVarName
      ? liveGroup?.toggleSFX?.[0]?.[slot.toggleVarName] === true
      : true;

    const arr   = liveGroup[slot.variableName];
    const files = Array.isArray(arr) ? arr.filter(item => item != null) : [];
    return { files, toggleEnabled };
  });

  // ── Helper: should this slot render as the empty "add audio" CTA? ──────
  // Show the CTA when the slot is off, when it has no files, or when it only
  // has a single disabled file. Disabled single-file slots should behave like
  // "empty but available to add", not like a faded configured row.
  const showAsCTA = (data) => {
    // console.log('PollAudioNode showAsCTA', data);
    if (!data.toggleEnabled) return true;
    if (data.files.length === 0) return true;
    if (data.files.length === 1 && !data.files[0]?.playSFX) return true;
    return false;
  };

  // ── Dynamic height ─────────────────────────────────────────────────────
  const computeTotalH = () => {
    let h = POLL_AUDIO_HEADER_H + TOP_PAD;
    slots.forEach((slot, i) => {
      const data = slotData[i] || { files: [], toggleEnabled: false };
      h += POLL_AUDIO_SLOT_LBL_H;
      if (showAsCTA(data)) {
        h += POLL_AUDIO_EMPTY_H;
      } else {
        const { files } = data;
        h += files.length * POLL_AUDIO_FILE_H + (files.length - 1) * POLL_AUDIO_FILE_GAP;
        if (slot.allowMultiple) h += POLL_AUDIO_FILE_GAP + POLL_AUDIO_ADD_BTN_H;
      }
      if (i < slots.length - 1) h += POLL_AUDIO_SLOT_GAP;
    });
    if (slots.length === 0) h += POLL_AUDIO_EMPTY_H;
    return h + BOTTOM_PAD;
  };

  const totalH    = computeTotalH();
  const x         = stepX - CARD_W / 2;
  const y         = stepY - totalH / 2 + TOP_PAD / 2;
  const slotW     = CARD_W - BODY_PAD * 2;
  const borderCol = isSelected ? C.purple : C.border;
  const hasParent = standaloneLayout && parentStepX !== null && parentStepX !== undefined;
  const hasNext = standaloneLayout && nextStep && nextStepX !== null && nextStepX !== undefined && nextStepY !== null && nextStepY !== undefined;
  const sourceX = hasParent ? parentStepX + (parentHalfW ?? CIRCLE_R) : null;
  const sourceY = parentStepY ?? stepY;
  const infoX = hasParent ? sourceX + PLUS_R + 32 : null;
  const addX = hasParent ? infoX + PLUS_R + 28 + PLUS_R : null;
  const branchMidX = hasParent ? Math.max(sourceX + BRANCH_STEM, (sourceX + x) / 2) : null;
  const rightEdgeX = stepX + CARD_W / 2;
  const exitSpineX = rightEdgeX + 34;
  const nextAnchorX = hasNext ? nextStepX - (nextStepHalfW || 0) : null;
  const branchColor = C.purple;

  // ── Single configured audio file row ──────────────────────────────────
  const renderFileRow = (item, fy, slotVarName, toggleVarName, fileIndex, isActiveSlot) => {
    const isDisabled  = item.playSFX === false;
    const rowOpacity  = isDisabled ? 0.38 : 1;
    const audioFieldInput = item?.audioFilename ?? (item?.srcUrl ? item : item?.filename);
    const audioField = normalizeAudioField(audioFieldInput);
    const resolvedName = audioField.name;
    const srcUrl = audioField.srcUrl
      ? audioField.srcUrl
      : audioField.name ? audioUrl(audioField.name, audioField.isDefault, assetFolder, campaignId) : null;
    const filename  = resolvedName || 'audio.ogg';
    const volume    = item.volume !== undefined ? item.volume : 100;
    const isLooping = item.loop === true;
    const nameShort = filename.length > 21 ? filename.slice(0, 19) + '…' : filename;
    const waveAreaY = fy + 27;
    const waveAreaW = slotW - 16;
    const volumeBadgeY = fy + POLL_AUDIO_FILE_H - 21;
    const volumeBadgeW = 54;
    const volumeBadgeX = x + BODY_PAD + slotW - 8 - volumeBadgeW;
    const loopIconSize = 18;
    const loopLabel = 'Looped';
    const loopLabelW = 34;
    const loopIconX = volumeBadgeX - loopIconSize - loopLabelW - 8;
    const fileLabelX = x + BODY_PAD + 28;

    // Active slot gets a green border/fill; disabled rows stay at reduced opacity
    const rowFill   = isActiveSlot && !isDisabled
      ? 'rgba(74,222,128,0.12)'
      : isDisabled ? 'rgba(145,70,255,0.04)' : 'rgba(145,70,255,0.09)';
    const rowStroke = isActiveSlot && !isDisabled
      ? 'rgba(74,222,128,0.75)'
      : isDisabled ? 'rgba(145,70,255,0.15)' : 'rgba(145,70,255,0.28)';
    const rowStrokeW = isActiveSlot && !isDisabled ? 1.5 : 1;

    return (
      <g
        style={{ cursor: 'pointer', opacity: rowOpacity }}
        onClick={(e) => { e.stopPropagation(); onActivateFile?.({ slotVarName, toggleVarName, fileIndex }); }}
      >
        <rect
          x={x + BODY_PAD} y={fy}
          width={slotW} height={POLL_AUDIO_FILE_H}
          rx={6} ry={6}
          fill={rowFill}
          stroke={rowStroke}
          strokeWidth={rowStrokeW}
          strokeDasharray={isDisabled ? '4 3' : undefined}
        />
        {/* ♪ filename */}
        <text x={x + BODY_PAD + 10} y={fy + 19}
          fontSize="14" fill={isActiveSlot && !isDisabled ? C.greenText : C.purple} style={{ userSelect: 'none' }}>♪</text>
        <text x={fileLabelX} y={fy + 19}
          fontSize="10.5" fontWeight="600" fill={C.text}
          style={{ userSelect: 'none' }}>{nameShort}</text>

        {isDisabled && (
          <text x={x + BODY_PAD + slotW / 2} y={fy + 19}
            fontSize="8.5" textAnchor="middle" fill={C.textMuted}
            style={{ userSelect: 'none' }}>(disabled)</text>
        )}

        {/* Volume badge */}
        {!isDisabled && (
          <>
            {isLooping && (
              <>
                <image
                  href={audioLoopIcon}
                  x={loopIconX}
                  y={volumeBadgeY}
                  width={loopIconSize}
                  height={loopIconSize}
                  style={{ filter: 'brightness(0) saturate(100%) invert(71%) sepia(39%) saturate(642%) hue-rotate(90deg) brightness(94%) contrast(89%)' }}
                />
                <text
                  x={loopIconX + loopIconSize + 4}
                  y={volumeBadgeY + 12}
                  fontSize="9"
                  fill={C.greenText}
                  style={{ userSelect: 'none', fontWeight: 600 }}
                >{loopLabel}</text>
              </>
            )}
            <rect x={volumeBadgeX} y={volumeBadgeY}
              width={volumeBadgeW} height={18} rx={5} ry={5}
              fill="rgba(255,255,255,0.07)" />
            <text x={volumeBadgeX + volumeBadgeW / 2} y={volumeBadgeY + 13}
              fontSize="9" fill={C.textMuted} textAnchor="middle"
              style={{ userSelect: 'none' }}>VOL {volume}%</text>
          </>
        )}

        {/* Waveform */}
        {!isDisabled && (
          <foreignObject
            x={x + BODY_PAD + 8} y={waveAreaY}
            width={waveAreaW} height={WAVE_H}
          >
            <AudioWaveformCell srcUrl={srcUrl} height={WAVE_H} />
          </foreignObject>
        )}

        {/* delete button */}
        {onRemoveFile && (
          <g
            style={{ cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); onRemoveFile({ slotVarName, toggleVarName, fileIndex }); }}
          >
            <circle cx={x + BODY_PAD + slotW - 9} cy={fy + 9} r={7} fill="#dc2626" />
            <g transform={`translate(${x + BODY_PAD + slotW - 9}, ${fy + 9})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              <rect x="-1.5" y="-4" width="3" height="1.5" rx="0.7" fill="#fee2e2" />
              <rect x="-3.5" y="-2.5" width="7" height="1.2" rx="0.6" fill="#fee2e2" />
              <rect x="-2.7" y="-1" width="5.4" height="4.8" rx="0.6" fill="#fee2e2" />
              <rect x="-1.2" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
              <rect x="0.5" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
            </g>
          </g>
        )}
      </g>
    );
  };

  // ── Empty-state / toggle-off CTA ──────────────────────────────────────
  const renderEmptyCTA = (ey, slotVarName, toggleVarName, isActiveSlot, fileIndex = null) => (
    <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onActivateFile?.({ slotVarName, toggleVarName, fileIndex }); }}>
      <rect x={x + BODY_PAD} y={ey}
        width={slotW} height={POLL_AUDIO_EMPTY_H}
        rx={6} ry={6}
        fill={isActiveSlot ? 'rgba(74,222,128,0.10)' : 'none'}
        stroke={isActiveSlot ? 'rgba(74,222,128,0.6)' : C.textDim}
        strokeWidth={isActiveSlot ? 1.5 : 1}
        strokeDasharray="4 3"
      />
      <text x={x + BODY_PAD + slotW / 2} y={ey + POLL_AUDIO_EMPTY_H / 2 - 4}
        fontSize="16" textAnchor="middle" fill={isActiveSlot ? C.greenText : C.textDim}
        style={{ userSelect: 'none' }}>+</text>
      <text x={x + BODY_PAD + slotW / 2} y={ey + POLL_AUDIO_EMPTY_H / 2 + 11}
        fontSize="9.5" textAnchor="middle" fill={isActiveSlot ? C.greenText : C.textDim}
        style={{ userSelect: 'none' }}>Click to add audio</text>
    </g>
  );

  // ── "＋ Add another file" button (shown below existing rows when allowMultiple) ─
  const renderAddMoreBtn = (ay, slotVarName, toggleVarName) => (
    <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onActivateFile?.({ slotVarName, toggleVarName, fileIndex: null }); }}>
      <rect x={x + BODY_PAD} y={ay}
        width={slotW} height={POLL_AUDIO_ADD_BTN_H}
        rx={6} ry={6}
        fill="none"
        stroke={`${C.greenText}88`} strokeWidth={1} strokeDasharray="4 3"
      />
      <text x={x + BODY_PAD + slotW / 2} y={ay + POLL_AUDIO_ADD_BTN_H / 2 + 4}
        fontSize="9.5" textAnchor="middle" fill={C.greenText}
        style={{ userSelect: 'none' }}>＋ Add another file</text>
    </g>
  );

  // ── Build slot list ───────────────────────────────────────────────────
  let curY = y + POLL_AUDIO_HEADER_H;

  const slotElements = slots.map((slot, i) => {
    const data        = slotData[i] || { files: [], toggleEnabled: false };
    const isCTA       = showAsCTA(data);
    const isActive    = activeSlotVarName === slot.variableName;
    const toggleVarName = slot.toggleVarName || slot.variableName;
    const labelY      = curY;
    curY += POLL_AUDIO_SLOT_LBL_H;
    const contentY    = curY;

    let content;
    if (isCTA) {
      // Always pass fileIndex: null for the CTA click — this routes to the re-enable
      // branch in handleFlowGraphicActivate (fileIndex === null) even for the single-
      // disabled-file case, so playSFX gets set back to true.
      content = renderEmptyCTA(contentY, slot.variableName, toggleVarName, isActive, null);
      curY += POLL_AUDIO_EMPTY_H;
    } else {
      const { files } = data;
      content = files.map((item, fi) => {
        const fy = contentY + fi * (POLL_AUDIO_FILE_H + POLL_AUDIO_FILE_GAP);
        return <g key={fi}>{renderFileRow(item, fy, slot.variableName, toggleVarName, fi, isActive)}</g>;
      });
      curY += files.length * POLL_AUDIO_FILE_H + (files.length - 1) * POLL_AUDIO_FILE_GAP;

      if (slot.allowMultiple) {
        curY += POLL_AUDIO_FILE_GAP;
        content = [
          ...content,
          <g key="add-more">{renderAddMoreBtn(curY, slot.variableName, toggleVarName)}</g>,
        ];
        curY += POLL_AUDIO_ADD_BTN_H;
      }
    }

    if (i < slots.length - 1) curY += POLL_AUDIO_SLOT_GAP;

    const showOffLabel = !data.toggleEnabled && !isCTA;
    // Label: green when active, dimmed when toggle off, normal otherwise
    const labelColor = isActive ? C.greenText : showOffLabel ? 'rgba(255,255,255,0.22)' : C.textMuted;

    return (
      <g key={slot.variableName || i}>
        {i > 0 && (
          <line
            x1={x + 14} y1={labelY - POLL_AUDIO_SLOT_GAP / 2}
            x2={x + CARD_W - 14} y2={labelY - POLL_AUDIO_SLOT_GAP / 2}
            stroke={C.border} strokeWidth={1}
          />
        )}
        <text
          x={x + BODY_PAD} y={labelY + 17}
          fontSize="9" fontWeight="700" fill={labelColor}
          style={{ userSelect: 'none' }}>
          {(slot.displayLabel || slot.variableName || '').toUpperCase()}
          {showOffLabel ? '  (off)' : ''}
        </text>
        {content}
      </g>
    );
  });

  return (
    <g className={standaloneLayout ? 'fc-node fc-node--audio-stack' : 'fc-node'} onClick={onActivate || undefined} style={{ cursor: onActivate ? 'pointer' : 'default' }}>
      {hasParent && !standaloneLayout && (
        <g style={{ pointerEvents: 'none' }}>
          <line x1={sourceX} y1={sourceY} x2={infoX - PLUS_R} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <line x1={infoX + PLUS_R} y1={sourceY} x2={addX - PLUS_R} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <line x1={addX + PLUS_R} y1={sourceY} x2={branchMidX} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          {Math.abs(sourceY - stepY) > 2 && (
            <line x1={branchMidX} y1={sourceY} x2={branchMidX} y2={stepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          )}
          <line x1={branchMidX} y1={stepY} x2={x} y2={stepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <circle cx={x} cy={stepY} r="3" fill={branchColor} fillOpacity="0.9" />
          <text x={addX} y={sourceY - PLUS_R - 8} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={C.greenText} style={{ userSelect: 'none' }}>
            {addButtonLabel || 'Add Audio'}
          </text>
        </g>
      )}

      {hasParent && !standaloneLayout && (
        <g onClick={(e) => { e.stopPropagation(); onInfoClick?.(); }} style={{ cursor: 'pointer' }}>
          <circle cx={infoX} cy={sourceY} r={PLUS_R} fill={C.neonBlue} />
          <text x={infoX} y={sourceY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            i
          </text>
        </g>
      )}

      {hasParent && !standaloneLayout && (
        <g onClick={(e) => { e.stopPropagation(); onAddItemClick?.(); }} style={{ cursor: 'pointer' }}>
          <circle cx={addX} cy={sourceY} r={PLUS_R} fill={C.greenText} />
          <text x={addX} y={sourceY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="300" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            +
          </text>
        </g>
      )}

      {/* Outer card */}
      <rect x={x} y={y} width={CARD_W} height={totalH}
        rx={BORDER_R} ry={BORDER_R}
        fill={C.card} stroke={borderCol} strokeWidth={1.5}
      />

      {/* Header band — clip has no rx so the bottom is cut flat; fill carries the rounded-top corners */}
      <clipPath id={`pa-hdr-${step.stepId}`}>
        <rect x={x} y={y} width={CARD_W} height={POLL_AUDIO_HEADER_H} />
      </clipPath>
      <rect x={x} y={y} width={CARD_W} height={POLL_AUDIO_HEADER_H + BORDER_R}
        rx={BORDER_R} ry={BORDER_R}
        clipPath={`url(#pa-hdr-${step.stepId})`}
        fill="rgba(145,70,255,0.20)"
      />
      <text x={x + 14} y={y + POLL_AUDIO_HEADER_H / 2 + 6}
        fontSize="16" fill={C.purple} style={{ userSelect: 'none' }}>♪</text>
      <text x={x + 36} y={y + POLL_AUDIO_HEADER_H / 2 + 6}
        fontSize="11" fontWeight="700" fill="var(--contrast-grey-1)"
        style={{ userSelect: 'none' }}>{step.stepTitle}</text>

      {slotElements}

      {hasNext && (
        <g style={{ pointerEvents: 'none' }}>
          <line x1={rightEdgeX} y1={stepY} x2={exitSpineX} y2={stepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          <circle cx={rightEdgeX} cy={stepY} r="3" fill={branchColor} fillOpacity="0.9" />
          {Math.abs(stepY - nextStepY) > 2 && (
            <line x1={exitSpineX} y1={Math.min(stepY, nextStepY)} x2={exitSpineX} y2={Math.max(stepY, nextStepY)} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          <line x1={exitSpineX} y1={nextStepY} x2={nextAnchorX} y2={nextStepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
        </g>
      )}
    </g>
  );
}
